/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import Head from 'next/head';
import { SmartLink as Link } from '../../../utils/smartLink';
import { getBaseSchemaUrl } from '../../../utils/common';
import cx from 'classnames';
import styles from './styles.module.scss';
import BreadcrumbSTARC from './__starc_components__/BreadcrumbSTARC';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { useLocale } from '@/hooks/useLocale';
import { type BreadcrumbModel } from '../../../../api/types/browse-search-types';
import { type IItem } from './utils/isBreadcrumbModel';
import { countryCodes } from '@/constants/locale';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';

const trackBreadCrumbInteraction = (linkName: string, breadCrumbValue: string) => {
  clickTrack({
    breadCrumbLinkName: linkName,
    eventType: 'breadcrumbLinkInteraction',
    breadCrumbValue: breadCrumbValue,
  });
};

type IProps = {
  data?: Array<IItem | BreadcrumbModel>;
  showSEOData?: boolean;
  customClass?: string;
  lastItemDisabled?: boolean;
  originalDesign?: boolean;
  classNames?: {
    nav?: string;
    item?: string;
    separator?: string;
  };
};

const Breadcrumb = ({
  data = [],
  showSEOData,
  customClass,
  lastItemDisabled = false,
  originalDesign = false,
  classNames,
}: IProps) => {
  const locale = useLocale();
  const baseSchemaURL = getBaseSchemaUrl(locale);
  const { setPageSpecificData } = usePageSpecificData();

  const setBreadcrumbAnalytics = (e: Event, item: IItem) => {
    const clickedUrl: string = item.url
      ? typeof item.url === 'string'
        ? item.url
        : item.url.pathname
      : '';

    if (item.url === '#') {
      e.preventDefault();
    }

    const breadcrumbList = data.map((el) => el.displayName);
    const breadCrumbValue = breadcrumbList.join('|');
    trackBreadCrumbInteraction(clickedUrl, breadCrumbValue);

    setPageSpecificData({
      productFindingMethod: 'Navigation',
      navigationLink: 'Breadcrumb',
      pfmLocation: 'Breadcrumb',
      navigationInteraction: 'navigationInteraction',
      eventType: '',
    });
  };

  return (
    <div
      data-testid="breadCrumb"
      className={cx(
        styles['breadcrumb-wrapper'],
        locale === countryCodes.ptBr && !originalDesign && styles.brazilBreadcrumb,
        customClass,
      )}
    >
      {data.length > 0 && (
        <BreadcrumbSTARC
          data={data}
          showSEOData={showSEOData}
          baseSchemaURL={baseSchemaURL}
          headComponent={Head}
          linkComponent={Link}
          onClick={(e, item) => setBreadcrumbAnalytics(e, item)}
          lastItemDisabled={lastItemDisabled}
          className={classNames?.nav}
          itemClassName={classNames?.item}
          separatorClassName={classNames?.separator}
        />
      )}
    </div>
  );
};

export default Breadcrumb;
